import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { FaUsers, FaExclamationTriangle, FaLaptop, FaUserCog, FaSitemap } from 'react-icons/fa';
import { Link } from 'react-router-dom';

export default function SessionManagement() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSessions = async () => {
    try {
      // Use the full URL to your API endpoint
      const apiUrl = 'https://dev.sliitleo.org/Backend/getSessionData.php';

      const response = await fetch(apiUrl, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (data && data.success) {
        setSessions(data.sessions || []);
      } else {
        const errorMsg = data?.error || 'Failed to load session data: Invalid response format';
        setError(errorMsg);
      }
    } catch (err) {
      //e.error('Fetch error:', err);
      setError(`Failed to load session data: ${err.message}. Please check your network connection and make sure the backend server is running.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);
  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    role: 'Member',
    image: '/user.png',
  };

  return (
    <>
      <Sidebar />
      <Topbar user={user} />

      <main className="lg:ml-64 mt-14 ml-0 px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
        {/* Header */}
        <div className="page-head">
          <div>
            <h1>Session Management</h1>
            <p className="page-desc">Active login sessions across the club portal.</p>
          </div>
        </div>

        {/* Navigation */}
        <div className="filter-row" style={{ flexWrap: 'wrap', marginBottom: 'var(--sp-5)' }}>
          <Link to="/admin" className="chip">
            <FaUserCog size={12} /> Role Management
          </Link>
          <Link to="/session-management" className="chip active">
            <FaUsers size={12} /> Session Management
          </Link>
          <Link to="/device-management" className="chip">
            <FaLaptop size={12} /> Device Management
          </Link>
          <Link to="/path-management" className="chip">
            <FaSitemap size={12} /> Role Path Management
          </Link>
        </div>

        <section className="panel">
          <div className="section-head">
            <h2>Active Sessions</h2>
            <span className="chip">{loading ? '…' : `${sessions.length} total`}</span>
          </div>

          {loading ? (
            <div className="empty-state">
              <span className="spinner lg" />
              <p>Loading sessions…</p>
            </div>
          ) : error ? (
            <div style={{ padding: '0 var(--sp-4) var(--sp-4)' }}>
              <div className="error-banner">
                <FaExclamationTriangle style={{ marginTop: 2, flexShrink: 0 }} />
                <span>{error}</span>
              </div>
              <button
                onClick={() => {
                  setError(null);
                  setLoading(true);
                  fetchSessions();
                }}
                className="btn btn-primary btn-sm"
                style={{ marginTop: 'var(--sp-3)' }}
              >
                Try Again
              </button>
            </div>
          ) : sessions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><FaUsers /></div>
              <h3>No active sessions</h3>
              <p>Sessions will appear here once members log in.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Last Login</th>
                    <th>Device</th>
                    <th style={{ textAlign: 'right' }}>Logins</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session) => (
                    <tr key={session.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div
                            style={{
                              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                              background: 'var(--surface-3)', display: 'grid', placeItems: 'center',
                              fontWeight: 700, fontSize: 13,
                            }}
                          >
                            {session.name?.[0]?.toUpperCase() || '?'}
                          </div>
                          <span className="truncate" style={{ maxWidth: 220 }} title={session.name || 'Unknown User'}>
                            {session.name || 'Unknown User'}
                          </span>
                        </div>
                      </td>
                      <td style={{ color: 'var(--text-2)' }}>{session.lastLogin || 'No login data'}</td>
                      <td>
                        {session.device ? (
                          <span className="pill pill-info"><span className="dot" />{session.device}</span>
                        ) : (
                          <span style={{ color: 'var(--text-3)' }}>—</span>
                        )}
                      </td>
                      <td className="num" style={{ textAlign: 'right' }}>
                        {session.logins || 0} {session.logins === 1 ? 'login' : 'logins'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </>
  );
}
